import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { StorageBrowser } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { SearchFieldComponent } from 'src/app/shared/search-field/search-field.component';
import { PaginationComponent } from 'src/app/shared/pagination/pagination.component';
import { AnnotationsService } from '../../services/annotations.service';
import { AnnotationsDialogComponent } from '../annotations-dialog/annotations-dialog.component';

@Component({
  selector: 'annotations',
  templateUrl: './annotations-list.component.html',
  styleUrls: ['./annotations-list.component.scss'],
  imports: [
    CommonModule,
    PaginationComponent,
    MatTableModule,
    MatSortModule,
    MatDialogModule,
    ReactiveFormsModule,
    MaterialModule,
    SearchFieldComponent,
  ],
})
export class AnnotationsListComponent implements OnInit, OnDestroy {
  metaData = {
    currentPage: 1,
    itemCount: 0,
    itemsPerPage: 30,
    totalItems: 0,
    totalPages: 0,
  };
  currUser: any;
  displayedColumns: string[] = ['typeId', 'eventType', 'label', 'comments'];
  filters = {
    q: '',
  };
  loader: boolean = false;
  list: any[] = [];

  @ViewChild(MatSort) sort: MatSort;
  dataSource;
  deepAnnotations;

  private latestRequestId = 0;
  private inFlightRequest?: Subscription;

  constructor(
    public userService: UserService,
    public ability: AbilityService,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private annotationsService: AnnotationsService,
    public storageBrowser: StorageBrowser
  ) {}

  onDebouncedSearch(value: string): void {
    this.filters.q = (value || '').trim();
    this.resetPagingAndFetch();
  }

  ngOnInit(): void {
    this.currUser = this.userService.currentUser.getValue();
    if (this.currUser.role != 'ADMIN') {
      this.displayedColumns.push('actions');
    }
    this.getAnnotationList();
  }

  ngOnDestroy(): void {
    this.inFlightRequest?.unsubscribe();
  }

  public getAnnotationList() {
    this.loader = true;

    const options: { page: number; limit: number; q?: string } = {
      page: this.metaData.currentPage,
      limit: this.metaData.itemsPerPage,
    };
    if (this.filters.q) {
      options.q = this.filters.q;
    }

    const requestId = ++this.latestRequestId;
    if (this.inFlightRequest) {
      this.inFlightRequest.unsubscribe();
    }
    this.inFlightRequest = this.annotationsService.getAnnotations(options).subscribe(
      (resp) => {
        if (requestId !== this.latestRequestId) {
          return;
        }
        this.loader = false;
        this.metaData = resp.meta;
        this.deepAnnotations = _.cloneDeep(resp.items);
        // One page at a time — the previous page's rows are replaced.
        this.list = resp.items;
        this.dataSource = new MatTableDataSource(this.list);
        setTimeout(() => {
          this.dataSource.sort = this.sort;
        }, 1000);
      },
      (error) => {
        if (requestId !== this.latestRequestId) {
          return;
        }
        this.loader = false;
        if (error?.error?.name === 'AccessDeniedError') {
          this.notificationService.error(
            'You are not eligible for Annotation'
          );
          this.router.navigate([this.ability.defaultPage()]);
        } else {
          this.notificationService.error(
            'Failed to load annotations. Please try again.'
          );
        }
      }
    );
  }

  private resetPagingAndFetch(): void {
    this.metaData.currentPage = 1;
    this.metaData.totalPages = 0;
    this.list = [];
    this.getAnnotationList();
  }

  createNewUser() {
    this.router.navigate(['settings/annotations/details']);
  }

  openDialog(annotation) {
    this.dialog
      .open(AnnotationsDialogComponent, { data: { name: annotation.name } })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.notificationService.success('Annotation Deleted Successfully');
          this.annotationsService
            .deleteAnnotations(annotation.id)
            .subscribe(() => {
              this.resetPagingAndFetch();
            });
        }
      });
  }

  ediAnnotation(user) {
    this.router.navigate(['settings/annotations/details'], {
      queryParams: { id: user.id },
    });
  }

  /** Pagination footer — load the requested page. */
  goToPage(page: number): void {
    if (this.loader || page === this.metaData.currentPage) {
      return;
    }
    this.metaData.currentPage = page;
    this.getAnnotationList();
  }

  onPageSizeChange(size: number): void {
    if (this.loader || size === this.metaData.itemsPerPage) return;
    this.metaData.itemsPerPage = size;
    this.metaData.currentPage = 1;
    this.getAnnotationList();
  }
}
